import { applyHomeEndKey, isCoarsePointer, shouldSendOnEnter } from "@recruiter/shared";
import { useRef, useState } from "react";

import { useSendSellerMessage } from "../../lib/mutations/seller";
import { SellerQueryError } from "../../lib/queries/seller";
import { postSellerTyping, uploadSellerFile } from "../../lib/seller-client";
import { CloseIcon, PaperclipIcon, SendIcon } from "../talent/icons";

// One file the talent has uploaded and is about to attach to their next message.
// `id` is the server-stored seller_files id (linked to the message on send);
// `name` is the picked filename, for the tray chip.
type Pending = { id: string; name: string };

// The talent's reply box. Sends the message (202 → enqueues STERLING's turn) and,
// optionally, files the talent attached: those are uploaded up-front (POST
// .../files → stored + scanned + extracted) and their ids ride the send so the
// backend links them to the message and STERLING reads them (load_seller_files).
// Errors surface inline, never swallowed (doctrine 1.5).
export function ReplyInput({ threadId, sellerId }: { threadId: string; sellerId: string }) {
  const [text, setText] = useState("");
  const [pending, setPending] = useState<Pending[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const mutation = useSendSellerMessage();

  // Grow the box with what the talent writes, up to a cap (then it scrolls). A layout read
  // on input, not a loop.
  function fit(el: HTMLTextAreaElement | null): void {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }
  // Throttle the "I'm composing" ping to at most once per 3s while typing; the
  // server's 8s freshness window + the client-side decay clear it when we stop.
  const lastTypingPingRef = useRef(0);

  function onChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    setText(e.target.value);
    fit(e.target);
    const now = Date.now();
    if (now - lastTypingPingRef.current > 3000) {
      lastTypingPingRef.current = now;
      void postSellerTyping(threadId, sellerId);
    }
  }

  async function onFilesPicked(list: FileList | null): Promise<void> {
    const files = list ? Array.from(list) : [];
    // Reset the input so picking the same file again re-fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      const outcome = await uploadSellerFile(threadId, files, sellerId);
      if (!outcome.ok) {
        setUploadError(outcome.message);
        return;
      }
      const rejected = outcome.data.rejected ?? [];
      // The backend stores files in order, skipping rejected ones — so the
      // returned ids line up with the accepted files in the same order.
      const rejectedNames = new Set(rejected.map((r) => r.file_name));
      const accepted = files.filter((f) => !rejectedNames.has(f.name));
      const ids = outcome.data.uploaded_file_ids ?? [];
      setPending((prev) => [
        ...prev,
        ...ids.map((id, i) => ({ id, name: accepted[i]?.name ?? "file" })),
      ]);
      if (rejected.length > 0) {
        setUploadError(rejected.map((r) => `${r.file_name}: ${r.reason}`).join("; "));
      }
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Couldn't upload that file.");
    } finally {
      setUploading(false);
    }
  }

  function removePending(id: string): void {
    setPending((prev) => prev.filter((p) => p.id !== id));
  }

  async function send(): Promise<void> {
    const body = text.trim();
    if ((!body && pending.length === 0) || mutation.isPending || uploading) return;
    try {
      await mutation.mutateAsync({
        threadId,
        body,
        sellerId,
        attachmentFileIds: pending.map((p) => p.id),
      });
      setText("");
      setPending([]);
      setUploadError(null);
      if (textRef.current) textRef.current.style.height = "";
    } catch {
      // Surfaced via mutation.error below — never swallowed.
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    // Desktop: Enter sends; Shift+Enter newlines. Touch: no Shift key exists,
    // so Enter always newlines and only the send button sends.
    if (shouldSendOnEnter(e, isCoarsePointer())) {
      e.preventDefault();
      void send();
      return;
    }
    // Home/End to the line start/end (Ctrl/Cmd: the whole reply). macOS gives a
    // textarea nothing for these keys on its own.
    applyHomeEndKey(e);
  }

  const sendDisabled = mutation.isPending || uploading || (!text.trim() && pending.length === 0);
  const errorMessage =
    uploadError ??
    (mutation.error instanceof SellerQueryError
      ? mutation.error.message
      : mutation.error
        ? "Couldn't send your reply. Try again."
        : null);

  return (
    <div className="sl-reply">
      {pending.length > 0 || uploading ? (
        <div className="sl-reply__tray">
          {pending.map((p) => (
            <span key={p.id} className="sl-reply__file">
              <PaperclipIcon size={13} />
              <span className="sl-reply__file-name">{p.name}</span>
              <button
                type="button"
                className="sl-reply__file-x"
                aria-label={`Remove ${p.name}`}
                onClick={() => removePending(p.id)}
              >
                <CloseIcon size={12} />
              </button>
            </span>
          ))}
          {uploading ? <span className="sl-hint">Uploading…</span> : null}
        </div>
      ) : null}
      <div className="sl-composer">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => void onFilesPicked(e.target.files)}
          aria-hidden="true"
        />
        <button
          className="sl-icon-btn"
          type="button"
          disabled={mutation.isPending || uploading}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach a file"
          title="Attach a file"
        >
          <PaperclipIcon size={18} />
        </button>
        <textarea
          ref={textRef}
          value={text}
          rows={1}
          placeholder="Reply to Mira: your price, timeline or questions"
          disabled={mutation.isPending}
          onChange={onChange}
          onKeyDown={onKeyDown}
          aria-label="Your reply"
        />
        <button
          className="sl-send"
          type="button"
          disabled={sendDisabled}
          onClick={() => void send()}
          aria-label={mutation.isPending ? "Sending" : "Send"}
          title="Send"
        >
          <SendIcon size={18} />
        </button>
      </div>
      {errorMessage ? <div className="sl-inline-error sl-reply__error">{errorMessage}</div> : null}
    </div>
  );
}
